/**
 * SwitchAccessibility
 *
 * Corrige el problema de accesibilidad de Element UI el-switch para cumplir
 * el patrón WAI-ARIA Switch (https://www.w3.org/WAI/ARIA/apg/patterns/switch/).
 *
 * Problema original:
 * - El div tiene role="switch" + aria-checked (correcto semánticamente)
 * - PERO el input interno es el que recibe foco por Tab (tabindex natural del checkbox)
 * - Resultado: el elemento con role="switch" no tiene foco → NVDA no anuncia cambios
 *
 * Solución:
 * - Hacer el div focusable (tabindex="0") → Tab aterriza en el div con role="switch"
 * - Ocultar el input del árbol de accesibilidad (aria-hidden, tabindex="-1")
 * - Añadir manejo de Space/Enter en el div (el div ya maneja click)
 * - Vue actualiza aria-checked en el div reactivamente → Chrome notifica → NVDA anuncia
 *
 * Con este enfoque:
 * - El div ES el widget switch (tiene role, aria-checked, foco, y keyboard)
 * - aria-checked se actualiza vía el binding reactivo de Vue (:aria-checked="checked")
 *   sin necesidad de mover atributos manualmente entre elementos
 * - NVDA anuncia cambios de estado porque el div con foco tiene aria-checked actualizado
 *
 * Registro en main.js:
 *   import { registerAccessibleElSwitch } from '@/components/directives/SwitchAccessibility.js'
 *   // Después de Vue.use(ElementUI, { locale })
 *   registerAccessibleElSwitch(Vue)
 */

/**
 * Registra el override accesible de ElSwitch.
 * Debe llamarse DESPUÉS de Vue.use(ElementUI).
 * @param {import('vue').VueConstructor} Vue
 */
export function registerAccessibleElSwitch (Vue) {
  const OriginalElSwitch = Vue.component('ElSwitch')
  if (!OriginalElSwitch) return

  Vue.component('ElSwitch', {
    extends: OriginalElSwitch,
    mounted () {
      setupAccessibleSwitch(this.$el)
    },
    updated () {
      ensureInputHidden(this.$el)
    }
  })
}

/**
 * Configura el div como widget switch accesible según patrón WAI-ARIA.
 * @param {HTMLElement} el - El elemento root (.el-switch div)
 */
function setupAccessibleSwitch (el) {
  if (!el) return
  const input = el.querySelector('input.el-switch__input')
  if (!input) return

  // El div ya tiene role="switch", aria-checked, aria-disabled del template de Element UI.
  // Solo necesitamos hacerlo focusable y manejar teclado.

  // Hacer el div focusable por Tab
  el.setAttribute('tabindex', '0')

  // Quitar el input del tab order y del árbol de accesibilidad
  input.setAttribute('tabindex', '-1')
  input.setAttribute('aria-hidden', 'true')

  // Vincular <label for="input-id"> al div mediante aria-labelledby.
  // <label for> solo funciona con elementos nativos labelable, no con div[role="switch"].
  // Buscamos la label asociada y la vinculamos con aria-labelledby (prioridad sobre aria-label).
  linkAssociatedLabel(el, input)

  // Manejar Space y Enter en el div (dispara el mismo handler que @click.prevent="switchValue")
  el.addEventListener('keydown', (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      el.click()
    }
  })

  // Si algo hace foco al input (ej: <label for="id">), redirigir al div
  input.addEventListener('focus', () => {
    el.focus()
  })
}

/**
 * Vincula la <label for="..."> asociada al input con el div mediante aria-labelledby.
 * Si la label no tiene id, le genera uno.
 * @param {HTMLElement} switchDiv - El div.el-switch
 * @param {HTMLElement} input - El input interno
 */
function linkAssociatedLabel (switchDiv, input) {
  const inputId = input.getAttribute('id')
  if (!inputId) return

  const label = document.querySelector('label[for="' + inputId + '"]')
  if (!label) return

  // Asegurar que la label tiene un id para poder referenciarla
  if (!label.id) {
    label.id = inputId + '-label'
  }

  // aria-labelledby tiene prioridad sobre aria-label en el cálculo del nombre accesible.
  // Esto hace que NVDA lea el texto completo de la label (incluidas opciones).
  switchDiv.setAttribute('aria-labelledby', label.id)
}

/**
 * Asegura que el input permanezca oculto para AT tras re-renders de Vue.
 * Vue podría resetear atributos del input en patches.
 * @param {HTMLElement} el
 */
function ensureInputHidden (el) {
  if (!el) return
  const input = el.querySelector('input.el-switch__input')
  if (!input) return

  if (input.getAttribute('tabindex') !== '-1') {
    input.setAttribute('tabindex', '-1')
  }
  if (input.getAttribute('aria-hidden') !== 'true') {
    input.setAttribute('aria-hidden', 'true')
  }
}
